/**
 * 头部组件
 * 参考：pro-components/src/layout/components/Header/index.tsx
 */

import { Layout } from 'antd';
import type { ReactNode } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { CollapsedIcon } from '../CollapsedIcon';
import type { ProRouteObject } from '../../typing';

const { Header: AntHeader } = Layout;

export interface HeaderProps {
  /** 是否收起 */
  collapsed?: boolean;
  /** 收起回调 */
  onCollapse?: (collapsed: boolean) => void;
  /** 是否允许折叠 */
  collapsible?: boolean;
  /** 右侧内容渲染 */
  rightContentRender?: () => ReactNode;
  /** 路由配置 */
  routes?: ProRouteObject[];
}

// 递归查找匹配的路由
const findMatchedRoute = (
  routes: ProRouteObject[],
  pathname: string
): ProRouteObject | null => {
  for (const route of routes) {
    if (route.path && matchPath(route.path, pathname)) {
      return route;
    }
    if (route.children) {
      const matched = findMatchedRoute(route.children, pathname);
      if (matched) return matched;
    }
  }
  return null;
};

export const Header: React.FC<HeaderProps> = ({
  collapsed,
  onCollapse,
  collapsible = true,
  rightContentRender,
  routes = [],
}) => {
  const location = useLocation();

  // 获取当前页面名称
  const currentRoute = findMatchedRoute(routes, location.pathname);
  const pageName = currentRoute?.name || '';

  const handleCollapse = () => {
    if (!collapsible) return;
    onCollapse?.(!collapsed);
  };

  return (
    <AntHeader
      className="pro-layout-header"
      style={{
        padding: '12px 16px',
        height: '72px',
        background: 'var(--theme-header-bg)',
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid var(--theme-border-color)',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
        position: 'sticky',
        top: 0,
        zIndex: 99,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: 12,
        }}
      >
        {/* 左侧：折叠按钮 + 页面名称 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: collapsible ? 12 : 0,
            minWidth: 0,
          }}
        >
          {collapsible && (
            <CollapsedIcon collapsed={collapsed} onClick={handleCollapse} />
          )}
          <div
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: 'var(--theme-text-primary)',
              lineHeight: 1.2,
              letterSpacing: '-0.01em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {pageName}
          </div>
        </div>

        {/* 右侧：自定义内容 */}
        {rightContentRender && (
          <div
            style={{
              padding: '4px 6px',
              borderRadius: 10,
              border: '1px solid var(--theme-border-color)',
              background: 'var(--theme-layout-bg)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
            }}
          >
            {rightContentRender()}
          </div>
        )}
      </div>
    </AntHeader>
  );
};
